import { claveDesdeEtiqueta } from '@mc/formularios'

import { valorInicial } from '../motor/esquema'
import { idDeFila } from '../motor/rutas'
import type { Bloque } from './catalogo'

/**
 * Lo que hace el constructor con la lista de campos de un formulario: añadir, mover,
 * duplicar y quitar, y el historial de deshacer y rehacer. La lista es la del documento tal
 * como la edita el motor (cada campo con su `blockType`), y lo que sale de aquí se guarda
 * con el mismo `PATCH` de siempre: el núcleo lo vuelve a validar al recibirlo.
 *
 * Funciones puras, con su prueba (`estado.test.ts`).
 */
export type Fila = Record<string, unknown>

const texto = (v: unknown): string => (typeof v === 'string' ? v : '')

/** Un identificador estable de un campo mientras se edita: el `id` que le dio el núcleo, o el `_clave` de uno nuevo. */
export const claveDeFila = (fila: Fila, indice: number): string =>
  String(fila.id ?? fila._clave ?? `i${indice}`)

/** Las claves que ya usan los campos (para no repetir), sin la de uno de ellos. */
export const clavesOcupadas = (campos: Fila[], salvo?: number): string[] =>
  campos
    .filter((_, i) => i !== salvo)
    .map((c) => texto(c.clave))
    .filter(Boolean)

/** Una copia sin los `id` del núcleo, a cualquier profundidad: lo que se duplica son filas nuevas, no las mismas. */
export const sinIds = (valor: unknown): unknown => {
  if (Array.isArray(valor)) return valor.map(sinIds)
  if (valor && typeof valor === 'object') {
    const salida: Fila = {}
    for (const [k, v] of Object.entries(valor as Fila))
      if (k !== 'id' && k !== '_clave') salida[k] = sinIds(v)
    return salida
  }
  return valor
}

const tieneCampo = (bloque: Bloque, nombre: string) =>
  bloque.campos.some((c) => c.nombre === nombre)

/**
 * Un campo recién añadido de un tipo: con lo que el núcleo pone por omisión en ese tipo,
 * una etiqueta que lo dice («Correo electrónico») y su clave sacada de ella. Las listas de
 * opciones nacen con dos, para que se vea qué es y no se pueda guardar vacía.
 */
export const nuevoCampo = (bloque: Bloque, campos: Fila[]): Fila => {
  const fila: Fila = { blockType: bloque.slug, ...valorInicial(bloque.campos), _clave: idDeFila() }
  const etiqueta = bloque.etiqueta
  if (tieneCampo(bloque, 'etiqueta') && !texto(fila.etiqueta)) fila.etiqueta = etiqueta
  if (tieneCampo(bloque, 'clave') && !texto(fila.clave))
    fila.clave = claveDesdeEtiqueta(texto(fila.etiqueta) || etiqueta, clavesOcupadas(campos))
  const opciones = bloque.campos.find((c) => c.nombre === 'opciones' && c.tipo === 'array')
  const subcampos = new Set((opciones?.campos ?? []).map((c) => c.nombre))
  if (
    opciones &&
    subcampos.has('etiqueta') &&
    subcampos.has('valor') &&
    (!Array.isArray(fila.opciones) || fila.opciones.length === 0)
  ) {
    fila.opciones = [1, 2].map((n) => ({
      etiqueta: `Opción ${n}`,
      valor: `opcion_${n}`,
      _clave: idDeFila(),
    }))
  }
  return fila
}

/** Inserta en `posicion` (0 = al principio; la longitud o más = al final). */
export const insertar = (campos: Fila[], fila: Fila, posicion: number): Fila[] => {
  const donde = Math.max(0, Math.min(posicion, campos.length))
  return [...campos.slice(0, donde), fila, ...campos.slice(donde)]
}

/**
 * Mueve el campo `desde` al hueco `hasta`: el hueco es la posición **antes** de mover (0 =
 * delante del primero, la longitud = detrás del último), que es lo que dice una línea al
 * arrastrar. Devuelve la lista nueva y dónde ha quedado el campo.
 */
export const mover = (
  campos: Fila[],
  desde: number,
  hasta: number,
): { campos: Fila[]; indice: number } => {
  if (desde < 0 || desde >= campos.length) return { campos, indice: desde }
  const hueco = Math.max(0, Math.min(hasta, campos.length))
  const destino = hueco > desde ? hueco - 1 : hueco
  if (destino === desde) return { campos, indice: desde }
  const copia = [...campos]
  const [fila] = copia.splice(desde, 1)
  copia.splice(destino, 0, fila!)
  return { campos: copia, indice: destino }
}

/** Un puesto arriba (-1) o abajo (+1): lo que hacen los botones y el teclado. */
export const desplazar = (
  campos: Fila[],
  indice: number,
  paso: -1 | 1,
): { campos: Fila[]; indice: number } => {
  const destino = indice + paso
  if (destino < 0 || destino >= campos.length) return { campos, indice }
  return mover(campos, indice, paso === 1 ? destino + 1 : destino)
}

/** Una copia del campo justo debajo, con «(copia)» en la etiqueta y una clave que no choca. */
export const duplicar = (campos: Fila[], indice: number): { campos: Fila[]; indice: number } => {
  const original = campos[indice]
  if (!original) return { campos, indice }
  const copia: Fila = { ...(sinIds(original) as Fila), _clave: idDeFila() }
  if (texto(original.etiqueta)) copia.etiqueta = `${texto(original.etiqueta)} (copia)`
  if (texto(original.clave))
    copia.clave = claveDesdeEtiqueta(
      texto(copia.etiqueta) || texto(original.clave),
      clavesOcupadas(campos),
    )
  return { campos: insertar(campos, copia, indice + 1), indice: indice + 1 }
}

export const quitar = (campos: Fila[], indice: number): Fila[] =>
  campos.filter((_, i) => i !== indice)

// --- Deshacer y rehacer ----------------------------------------------------------------

/**
 * El historial: los documentos de antes (`pasado`) y los deshechos (`futuro`). Escribir en
 * el mismo control seguido es **un** paso, no uno por tecla: lo que se escribe en la misma
 * ruta con menos de `JUNTAR_MS` entre tecla y tecla se junta con lo anterior.
 */
export type Historial<T> = {
  pasado: T[]
  futuro: T[]
  ultimaRuta: null | string
  ultimaVez: number
}

export const JUNTAR_MS = 1000
export const MAXIMO_DE_PASOS = 100

export const historialVacio = <T>(): Historial<T> => ({
  pasado: [],
  futuro: [],
  ultimaRuta: null,
  ultimaVez: 0,
})

/** Apunta un cambio. `antes` es el documento tal como estaba. */
export const registrar = <T>(
  h: Historial<T>,
  ruta: string,
  antes: T,
  ahora: number,
): Historial<T> => {
  if (ruta === h.ultimaRuta && ahora - h.ultimaVez < JUNTAR_MS) return { ...h, ultimaVez: ahora }
  return {
    pasado: [...h.pasado, antes].slice(-MAXIMO_DE_PASOS),
    futuro: [],
    ultimaRuta: ruta,
    ultimaVez: ahora,
  }
}

/** Lo siguiente que se apunte será un paso propio, aunque sea en la misma ruta (añadir dos campos seguidos son dos pasos). */
export const cortar = <T>(h: Historial<T>): Historial<T> => ({ ...h, ultimaRuta: null })

export const deshacer = <T>(
  h: Historial<T>,
  actual: T,
): null | { historial: Historial<T>; valores: T } => {
  const previo = h.pasado.at(-1)
  if (previo === undefined) return null
  return {
    historial: {
      pasado: h.pasado.slice(0, -1),
      futuro: [actual, ...h.futuro],
      ultimaRuta: null,
      ultimaVez: 0,
    },
    valores: previo,
  }
}

export const rehacer = <T>(
  h: Historial<T>,
  actual: T,
): null | { historial: Historial<T>; valores: T } => {
  const [siguiente, ...resto] = h.futuro
  if (siguiente === undefined) return null
  return {
    historial: { pasado: [...h.pasado, actual], futuro: resto, ultimaRuta: null, ultimaVez: 0 },
    valores: siguiente,
  }
}
